import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { fetchQuotations } from '../../lib/consoleQuotations';
import { displayCategoryForCode } from '../../lib/factoryCategories';
import { relativeTime } from '../../lib/relativeTime';
import { activityLevel, hasActivity, activityLabel } from '../../lib/requestActivity';
import { heldChipLabel } from '../../lib/offerVisibility';
import { adminFeeStatusLabel, adminFeeBadgeTone } from '../../lib/adminRequestFees';
import InviteModal from './InviteModal';
import DistributePanel from './DistributePanel';

const nf = (v) => (v && v !== 'not_found' ? String(v).trim() : '');
const fmtDate = (iso, isAr) => (iso ? new Date(iso).toLocaleDateString(isAr ? 'ar' : 'en', { day: 'numeric', month: 'short' }) : null);

export const STATUS = {
  waiting: { ar: 'بانتظار المورّد', en: 'Waiting supplier', cls: 'warn' },
  replied: { ar: 'ردّ المورّد', en: 'Supplier replied', cls: 'ok' },

  // ══ WHAT USED TO BE ONE GREY «مغلق» ═══════════════════════════
  // `closed` held five raw statuses at once (consoleQuotations.js), so the
  // best outcome on this board — the trader chose someone — wore the same
  // grey as a rejected request. Three values now, and the colours say what
  // the admin should DO, not merely what happened:
  //   accepted  green  — good news, nothing owed
  //   completed neutral — over; there is no work left on this row
  //   rejected  red    — a dead ending, like cancelled
  //
  // ⚠️ `completed` is NEUTRAL here although AdminStatusBadge paints the word
  // green. Deliberate, and the reason is that this board already spends green
  // on `replied` and now on `accepted`: a third would leave three of six chips
  // indistinguishable at a glance, which is the problem being fixed. If the
  // palette ever gains a "done" tone, this is the line that wants it.
  accepted: { ar: 'تم قبول عرض', en: 'Offer accepted', cls: 'ok' },
  completed: { ar: 'مكتمل', en: 'Completed', cls: 'neutral' },
  rejected: { ar: 'مرفوض', en: 'Rejected', cls: 'danger' },

  // Not a variant of any of the above. A completed deal and a trader who
  // walked away are different business facts, and the one worth counting is
  // the one that was being hidden. Red, matching the trader's and the
  // supplier's badge, so the same event reads alike on all three surfaces.
  cancelled: { ar: 'ملغي', en: 'Cancelled', cls: 'danger' },
};

// Every key of STATUS, in the order the chips show them. Derived from the map
// rather than written out again: the chip row and the counter used to be two
// literal lists, and a value added to one and not the others rendered a chip
// with no count, or a badge that crashed on `sc.cls`.
const STATUS_KEYS = Object.keys(STATUS);

// A row whose status is not in the map at all. It cannot happen from
// buildQuotationRows today, and that is exactly why it is worth handling: the
// old code read STATUS[r.status].cls unguarded, so the first unmapped value
// would have taken the whole board down with a TypeError rather than showing
// one odd chip.
const STATUS_FALLBACK = { ar: 'أخرى', en: 'Other', cls: 'neutral' };

// ── Four streams, picked before the list ───────────────────────────────────
//
// They were one list with a type chip, and the chip had two values for four
// things — so a managed order, an idea-to-product brief and an open RFQ all read
// as "Managed" and the Open button sent them to the same screen.
//
// They are not variants of one request. The first two are a SERVICE we run: the
// order is ours to move through stages. The last two are a MARKET act: get the
// request in front of factories and wait for offers. Different work, different
// screen, different verb -- so the page asks which one you came for, instead of
// mixing them and labelling the mix.
const KINDS = [
  {
    k: 'managed',
    ar: 'طلبات مُدارة', en: 'Managed orders',
    subAr: 'خدمة نديرها نحن من أول الطلب إلى التسليم',
    subEn: 'A service we run end to end, from request to delivery',
    cls: 'ok',
  },
  {
    k: 'idea_to_product',
    ar: 'تصنيع الفكرة', en: 'Idea to product',
    subAr: 'التاجر عنده فكرة لا منتج — نحوّلها إلى شيء قابل للتصنيع',
    subEn: 'The trader has an idea, not a product — we turn it into something makeable',
    cls: 'info',
  },
  {
    k: 'factory', ar: 'عرض سعر من مصنع محدّد', en: 'Quote from one factory',
    subAr: 'طلب موجّه لمصنع بعينه من كتالوجنا',
    subEn: 'Aimed at one named factory from our catalog',
    cls: 'neutral',
  },
  {
    // ⚠️ "مفتوح" WAS A CONTRADICTION NEXT TO THE STATUS BADGE. This chip
    // is the request's TYPE and never changes; the badge beside it is the
    // request's STATE and does. A closed request therefore rendered
    // «مغلق» and «طلب عروض مفتوح» side by side, and the admin had to
    // work out which of the two words was about the lifecycle.
    // «مباشر» describes how the request was RAISED — straight to the
    // market rather than aimed at one factory — which is a fact about the
    // type and stays true whatever the state.
    k: 'open', ar: 'طلب عروض مباشر', en: 'Direct request for offers',
    subAr: 'رفعه التاجر للسوق — نوزّعه على مصانع الفئة',
    subEn: 'Posted to the market — we distribute it to the category',
    cls: 'warn',
  },
];
const KIND_BY_KEY = Object.fromEntries(KINDS.map((k) => [k.k, k]));

// Managed and idea-to-product have an order screen; the other two do not, and
// their work is the Distribute block on the row itself.
const HAS_ORDER_SCREEN = new Set(['managed', 'idea_to_product']);

export default function ConsoleQuotations({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');
  const [q, setQ] = useState('');
  const [st, setSt] = useState('all');     // all | waiting | replied | closed
  // Which stream. Empty means the picker. In the URL so it survives a refresh and
  // can be linked to, the same way the suppliers screen carries its stage.
  const [params, setParams] = useSearchParams();
  const kind = KIND_BY_KEY[params.get('kind')] ? params.get('kind') : '';
  const setKind = (k) => setParams(k ? { kind: k } : {}, { replace: true });
  const [invite, setInvite] = useState(null); // { factoryId, request }
  // Which request has its Distribute block open. One at a time: each open panel
  // runs its own factory query, and several expanded at once would fire a
  // request per keystroke across all of them.
  const [distributing, setDistributing] = useState(null);

  useEffect(() => { fetchQuotations().then(setRows).catch((e) => { setErr(e.message || 'load failed'); setRows([]); }); }, []);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 1900); };
  const title = (r) => (isAr ? (nf(r.title_ar) || nf(r.title_en)) : (nf(r.title_en) || nf(r.title_ar))) || (isAr ? 'طلب' : 'Request');
  const trader = (r) => nf(r.trader?.company_name) || nf(r.trader?.full_name) || (isAr ? 'تاجر' : 'Trader');

  // Per-stream totals for the picker cards, and status counts WITHIN the chosen
  // stream: "3 waiting" has to mean three in what you are looking at, not three
  // across four unrelated kinds of work.
  const byKind = useMemo(() => {
    const c = Object.fromEntries(KINDS.map((k) => [k.k, 0]));
    (rows || []).forEach((r) => { if (c[r.kind] != null) c[r.kind] += 1; });
    return c;
  }, [rows]);

  const inKind = useMemo(
    () => (rows || []).filter((r) => !kind || r.kind === kind),
    [rows, kind],
  );

  const counts = useMemo(() => {
    const c = { all: 0, ...Object.fromEntries(STATUS_KEYS.map((k) => [k, 0])) };
    inKind.forEach((r) => { c.all++; if (c[r.status] != null) c[r.status]++; });
    return c;
  }, [inKind]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    return inKind.filter((r) => {
      if (st !== 'all' && r.status !== st) return false;
      if (s && ![title(r), trader(r), r.factory?.name].filter(Boolean).join(' ').toLowerCase().includes(s)) return false;
      return true;
    });
    // eslint-disable-next-line
  }, [rows, inKind, q, st, isAr]);

  const copyShare = (r) => {
    if (r.invite_slug) { navigator.clipboard.writeText(`${window.location.origin}/f/${r.invite_slug}`); flash(isAr ? 'نُسخ رابط الطلب' : 'Request link copied'); }
    else flash(isAr ? 'لا يوجد رابط مشاركة لهذا الطلب' : 'No share link for this request');
  };

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="quotations">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">
              {kind ? (isAr ? KIND_BY_KEY[kind].ar : KIND_BY_KEY[kind].en) : (isAr ? 'طلبات التجار' : 'Trader Requests')}
            </h1>
            <p className="ac-sub">
              {rows == null ? '…'
                : kind
                  ? (isAr ? KIND_BY_KEY[kind].subAr : KIND_BY_KEY[kind].subEn)
                  : (isAr ? 'اختر نوع الطلب الذي جئت من أجله' : 'Pick the kind of request you came for')}
            </p>
          </div>
          {kind && (
            <button className="ac-btn" onClick={() => { setKind(''); setSt('all'); setQ(''); }}>
              {isAr ? '← كل الأنواع' : '← All kinds'}
            </button>
          )}
        </div>

        {err && !kind && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{err}</p>}

        {/* The picker. Four cards, one per kind of work. */}
        {!kind && (
          <div className="ac-signups" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
            {rows == null
              ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="ac-skel" style={{ height: 124 }} />)
              : KINDS.map((k) => (
                <button key={k.k} type="button" className="ac-signup" onClick={() => setKind(k.k)}>
                  <p className="ac-signup-lbl">{isAr ? k.ar : k.en}</p>
                  <span className="ac-signup-num"><b>{byKind[k.k]}</b></span>
                  <p className="ac-signup-foot" style={{ whiteSpace: 'normal' }}>{isAr ? k.subAr : k.subEn}</p>
                </button>
              ))}
          </div>
        )}

        {kind && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
          <input className="ac-input" style={{ maxWidth: 420 }} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={isAr ? 'ابحث بالمنتج أو التاجر أو المصنع…' : 'Search product, trader, factory…'} />
          <div className="ac-chiprow">
            {['all', ...STATUS_KEYS].map((k) => (
              <button key={k} className={`ac-chip${st === k ? ' on' : ''}`} onClick={() => setSt(k)}>
                {k === 'all' ? (isAr ? 'الكل' : 'All') : (isAr ? STATUS[k].ar : STATUS[k].en)}
                <span style={{ opacity: 0.6 }}>{counts[k]}</span>
              </button>
            ))}
          </div>
        </div>
        )}

        {err && kind && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{err}</p>}

        {!kind ? null : rows == null ? (
          <div className="ac-skel" style={{ height: 90 }} />
        ) : filtered.length === 0 ? (
          <div className="ac-placeholder">{isAr ? 'لا طلبات مطابقة.' : 'No matching requests.'}</div>
        ) : filtered.map((r) => {
          const sc = STATUS[r.status] || STATUS_FALLBACK;
          const level = activityLevel({ ...r, waiting: r.status === 'waiting' });
          const moved = hasActivity(r);
          return (
            <div className="ac-card" key={r.id} style={{ display: 'flex', gap: 14, padding: 14, marginBottom: 10, alignItems: 'flex-start',
              borderInlineStart: level === 'ok' ? undefined : `3px solid ${level === 'overdue' ? 'var(--ac-danger)' : 'var(--ac-warn)'}` }}>
              {r.image
                ? <img src={r.image} alt="" loading="lazy" style={{ width: 74, height: 74, borderRadius: 12, objectFit: 'cover', flexShrink: 0, background: 'var(--ac-sunk)' }} />
                : <div style={{ width: 74, height: 74, borderRadius: 12, flexShrink: 0, background: 'var(--ac-sunk)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ac-faint)', fontSize: 11 }}>{isAr ? 'بلا صورة' : 'No img'}</div>}

              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--ac-ink)' }}>{title(r)}</span>
                  <span className={`ac-badge ${sc.cls}`}><span className="dot" />{isAr ? sc.ar : sc.en}</span>
                  <span className={`ac-badge ${KIND_BY_KEY[r.kind]?.cls || 'neutral'}`}>
                    {isAr ? (KIND_BY_KEY[r.kind]?.ar || r.kind) : (KIND_BY_KEY[r.kind]?.en || r.kind)}
                  </span>
                  {level !== 'ok' && <span className={`ac-badge ${level === 'overdue' ? 'danger' : 'warn'}`}><span className="dot" />{activityLabel(level, isAr)}</span>}
                  {/* Quotes the trader cannot see yet. "Supplier replied" was
                      already true for these — offers_select shows an admin
                      every offer — but it read as a request needing nothing,
                      when in fact it is waiting on someone here approving the
                      supplier. Absent on every request with nothing held, so
                      it stays quiet in the normal case. */}
                  {r.heldCount > 0 && (
                    <span className="ac-badge warn" title={isAr
                      ? 'عروض محجوزة عن التاجر حتى يُوثَّق المورّد'
                      : 'Quotes held from the trader until the supplier is verified'}>
                      <span className="dot" />{heldChipLabel(r.heldCount, lang)}
                    </span>
                  )}
                  {/* The request fee. Unpaid = waiting on the trader, and
                      not distributable (offerable is false; the server refuses). */}
                  {r.feeStatus && (
                    <span className={`ac-badge ${adminFeeBadgeTone(r.feeStatus)}`}>
                      <span className="dot" />{isAr ? 'الرسوم: ' : 'Fee: '}{adminFeeStatusLabel(r.feeStatus, isAr)}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--ac-muted)', marginTop: 5 }}>
                  {trader(r)}{nf(r.trader?.country) ? ` · ${r.trader.country}` : ''}{r.factory?.name ? `  →  ${r.factory.name}` : ''}
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: 'var(--ac-faint)', flexWrap: 'wrap' }}>
                  {/* Timestamps first, so managed and factory cards read identically */}
                  <span>{isAr ? 'وصل' : 'Submitted'}: {relativeTime(r.created_at, isAr)}</span>
                  {moved ? <span>{isAr ? 'آخر نشاط' : 'Last activity'}: {relativeTime(r.last_activity_at, isAr)}</span> : null}
                  {r.quantity ? <span>{isAr ? 'الكمية' : 'Qty'}: {r.quantity}{r.unit ? ` ${r.unit}` : ''}</span> : null}
                  {r.target_price ? <span>{isAr ? 'السعر المستهدف' : 'Target'}: {r.target_price} {nf(r.currency)}</span> : null}
                  {r.attachments > 0 ? <span>📎 {r.attachments}</span> : null}
                  {/* Routed-to count. ZERO is the important value and is shown
                      loudly: since 2026-09-19 an unrouted request is invisible
                      to every supplier, so 0 means this request is sitting
                      there doing nothing. Shown here, not only in the detail
                      page, so the board answers "what have I not sent out?"
                      at a glance. */}
                  {r.routedCount > 0
                    ? <span>{isAr ? 'موجَّه إلى' : 'Routed to'}: {r.routedCount}</span>
                    : <span style={{ color: 'var(--ac-danger, #c0392b)', fontWeight: 600 }}>
                        {isAr ? 'لم يُوجَّه لأحد' : 'Not routed'}
                      </span>}
                  {r.deadline ? <span>{isAr ? 'الموعد' : 'Due'}: {fmtDate(r.deadline, isAr)}</span> : null}
                  {r.ship_to ? <span>{isAr ? 'الشحن' : 'Ship'}: {r.ship_to}</span> : null}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                  {/* Only the two service kinds have an order screen. A factory
                      quote and an open RFQ have no stages to move through: their
                      work is the Distribute block on this row, and sending them
                      to the managed-order screen — which is what the old
                      condition did for everything non-factory — showed an order
                      pipeline for something that is not an order. */}
                  {HAS_ORDER_SCREEN.has(r.kind)
                    ? <button className="ac-btn ac-btn-sm" onClick={() => nav(`/admin2/managed/${r.id}`)}>{isAr ? 'فتح الطلب' : 'Open order'}</button>
                    /* The other two now open too. Until 2026-09-18 they could
                       only be distributed from the row, which meant deciding who
                       should see a request without being able to read it. */
                    : <button className="ac-btn ac-btn-sm" onClick={() => nav(`/admin2/requests/${r.id}`)}>{isAr ? 'فتح الطلب' : 'Open request'}</button>}
                  <button className="ac-btn ac-btn-sm" onClick={() => copyShare(r)} disabled={!r.invite_slug} style={{ opacity: r.invite_slug ? 1 : 0.5 }}>{isAr ? 'رابط المشاركة' : 'Share link'}</button>
                  {r.factory && <button className="ac-btn ac-btn-sm" onClick={() => setInvite({ factoryId: r.factory.id, request: r })} title={isAr ? 'راسل المصنع بقالب جاهز' : 'Message with a ready template'}>WhatsApp</button>}
                  {r.factory && <button className="ac-btn ac-btn-sm" onClick={() => setInvite({ factoryId: r.factory.id, request: r })}>{isAr ? 'إرسال دعوة' : 'Send invitation'}</button>}
                  <button className={`ac-btn ac-btn-sm${distributing === r.id ? ' ac-btn-primary' : ''}`}
                    onClick={() => setDistributing(distributing === r.id ? null : r.id)}
                    title={isAr ? 'وزّع هذا الطلب على عدة مصانع' : 'Distribute this request to several factories'}>
                    {isAr ? 'توزيع' : 'Distribute'}
                  </button>
                </div>

                {distributing === r.id && (
                  <DistributePanel
                    requestId={r.id}
                    // Only title_ar/_en/_zh are read from this, to resolve {{2}}
                    // for the send preview's message bubble.
                    //
                    // ⚠️ `r.status` on THIS row is the derived board vocabulary,
                    // not requests.status — see consoleQuotations.js. Never let
                    // a panel infer offerability from it; `offerable` below is
                    // computed there from the raw column.
                    request={r}
                    offerable={!!r.offerable}
                    // NOT r.status — that one is the derived board vocabulary
                    // (see the warning above). raw_status is the real column,
                    // kept alongside it in consoleQuotations.js precisely so
                    // the panel can tell "cancelled" from "offer accepted".
                    rawStatus={r.raw_status}
                    isAr={isAr}
                    flash={flash}
                    initialCategory={r.category ? displayCategoryForCode(r.category)?.key : undefined}
                    // The panel does not own the modal: ConsoleQuotations already
                    // mounts exactly one InviteModal for the whole page, and a
                    // second instance inside an expandable row would stack over
                    // it. The panel hands up a factory id; the page decides.
                    onOpenInvite={(factoryId) => setInvite({ factoryId, request: r })}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      {invite && <InviteModal factoryId={invite.factoryId} request={invite.request} isAr={isAr} onClose={() => setInvite(null)} flash={flash} />}
      {toast && <div className="ac-toast" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>{toast}</div>}
    </ConsoleShell>
  );
}
